/**
 * Lightweight per-browser preferences (localStorage). Used to remember a user's
 * preferred Blue Sky look so it pre-loads in the editor each time. This is
 * browser-scoped; it can later be promoted to an account-level setting (a
 * `profiles` column or a settings table) without changing call sites.
 */

import { DEFAULT_SKY_STYLE, SkyStyle, isSkyStyle } from './gemini';
import { DEFAULT_VERTICALS, VerticalsOptions } from './verticals';

const SKY_STYLE_KEY = 'hdrphoto.blueSky.style';
const VERTICALS_KEY = 'hdrphoto.verticals.default';

export function getSkyStyleDefault(): SkyStyle {
  try {
    const raw = window.localStorage.getItem(SKY_STYLE_KEY);
    return isSkyStyle(raw) ? raw : DEFAULT_SKY_STYLE;
  } catch {
    return DEFAULT_SKY_STYLE;
  }
}

export function setSkyStyleDefault(style: SkyStyle): void {
  try {
    window.localStorage.setItem(SKY_STYLE_KEY, style);
  } catch {
    /* ignore quota / privacy-mode errors */
  }
}

const clamp01 = (v: any, fallback: number, min: number, max: number): number =>
  typeof v === 'number' && Number.isFinite(v)
    ? Math.min(max, Math.max(min, v))
    : fallback;

export function getVerticalsDefault(): VerticalsOptions {
  try {
    const raw = window.localStorage.getItem(VERTICALS_KEY);
    if (!raw) return { ...DEFAULT_VERTICALS };
    const p = JSON.parse(raw);
    return {
      vertical: clamp01(p?.vertical, DEFAULT_VERTICALS.vertical, -1, 1),
      horizontal: clamp01(p?.horizontal, DEFAULT_VERTICALS.horizontal, -1, 1),
      rotate: clamp01(p?.rotate, DEFAULT_VERTICALS.rotate, -15, 15),
      autoFit:
        typeof p?.autoFit === 'boolean' ? p.autoFit : DEFAULT_VERTICALS.autoFit,
    };
  } catch {
    return { ...DEFAULT_VERTICALS };
  }
}

export function setVerticalsDefault(options: VerticalsOptions): void {
  try {
    window.localStorage.setItem(VERTICALS_KEY, JSON.stringify(options));
  } catch {
    /* ignore quota / privacy-mode errors */
  }
}
